import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from './ui/pagination';
import { 
  ChevronDown, 
  ChevronRight, 
  ExternalLink, 
  Clock,
  User,
  Calendar,
  Target,
  FileSearch
} from 'lucide-react';
import { BatchItem } from './BatchApproval';

interface BatchTableProps {
  data: BatchItem[];
  selectedItems: Set<string>;
  onSelectAll: (checked: boolean) => void;
  onSelectItem: (itemId: string, checked: boolean) => void;
  onRowClick: (item: BatchItem) => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

export function BatchTable({
  data,
  selectedItems,
  onSelectAll,
  onSelectItem,
  onRowClick,
  isAllSelected,
  isIndeterminate,
  currentPage,
  totalPages,
  totalItems,
  onPageChange
}: BatchTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpansion = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click when expanding
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedRows(newExpanded);
  };

  const handleRowClick = (item: BatchItem, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    
    // Don't trigger row click if user clicked on checkbox, button, or other interactive elements
    if (target.closest('input[type="checkbox"]') || 
        target.closest('button') || 
        target.closest('[role="button"]')) {
      return;
    }
    
    onRowClick(item);
  };

  const handleCheckboxClick = (itemId: string, checked: boolean, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click when checking checkbox
    onSelectItem(itemId, checked);
  };

  const handleViewClick = (item: BatchItem, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click when clicking view button
    onRowClick(item);
  };

  const getStatusBadge = (status: BatchItem['status']) => {
    const statusConfig = {
      'Pending': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      'Auto-Approved': { color: 'bg-green-100 text-green-800 border-green-200', icon: Clock },
      'Auto-Rejected': { color: 'bg-red-100 text-red-800 border-red-200', icon: Clock },
      'Approved': { color: 'bg-green-100 text-green-800 border-green-200', icon: User },
      'Rejected': { color: 'bg-red-100 text-red-800 border-red-200', icon: User },
      'Skipped': { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: Clock }
    };

    const config = statusConfig[status];
    const IconComponent = config.icon;

    return (
      <Badge variant="outline" className={`${config.color} gap-1`} style={{ fontSize: '9.6px' }}>
        <IconComponent className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getAiSuggestionBadge = (suggestion: 'Compliant' | 'Deficient') => {
    return (
      <Badge 
        variant="outline" 
        className={`${suggestion === 'Compliant' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}
        style={{ fontSize: '9.6px' }}
      >
        {suggestion}
      </Badge>
    );
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600';
    if (confidence >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onCheckedChange={onSelectAll}
                  aria-label="Select all items on this page"
                />
                <span className="text-gray-700" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                  Select All ({data.length} items)
                </span>
              </div>
              {selectedItems.size > 0 && (
                <span className="text-blue-600" style={{ fontSize: '11.2px' }}>
                  {selectedItems.size} selected
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-gray-500" style={{ fontSize: '9.6px' }}>
                Click any row to view audit goals detail
              </span>
              <span className="text-gray-500" style={{ fontSize: '9.6px' }}>
                Showing {((currentPage - 1) * 50) + 1}-{Math.min(currentPage * 50, totalItems)} of {totalItems}
              </span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-12"></TableHead>
                <TableHead className="w-12"></TableHead>
                <TableHead style={{ fontSize: '11.2px', fontWeight: 500 }}>Client / Visit</TableHead>
                <TableHead style={{ fontSize: '11.2px', fontWeight: 500 }}>Clinician</TableHead>
                <TableHead style={{ fontSize: '11.2px', fontWeight: 500 }}>Goal Summary</TableHead>
                <TableHead style={{ fontSize: '11.2px', fontWeight: 500 }}>AI Suggestion</TableHead>
                <TableHead style={{ fontSize: '11.2px', fontWeight: 500 }}>Confidence</TableHead>
                <TableHead style={{ fontSize: '11.2px', fontWeight: 500 }}>Status</TableHead>
                <TableHead style={{ fontSize: '11.2px', fontWeight: 500 }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((item) => (
                <React.Fragment key={item.id}>
                  <TableRow 
                    className={`${selectedItems.has(item.id) ? 'bg-blue-50' : ''} ${item.status.startsWith('Auto-') ? 'bg-green-50/50' : ''} hover:bg-gray-50 cursor-pointer transition-colors`}
                    onClick={(event) => handleRowClick(item, event)}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedItems.has(item.id)}
                        onCheckedChange={(checked) => handleCheckboxClick(item.id, checked as boolean, event as any)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Select ${item.clientName}`}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(event) => toggleRowExpansion(item.id, event)}
                        className="h-6 w-6 p-0"
                      >
                        {expandedRows.has(item.id) ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                      </Button>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                            {item.clientName}
                          </span>
                          <Badge variant="outline" style={{ fontSize: '8px' }}>
                            {item.clientId}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500" style={{ fontSize: '9.6px' }}>
                          <Calendar className="w-3 h-3" />
                          {item.visitDate}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                          {item.clinicianName}
                        </div>
                        <div className="text-gray-500" style={{ fontSize: '9.6px' }}>
                          {item.clinicianId}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell className="max-w-xs">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="space-y-1">
                              <div className="text-gray-900 truncate" style={{ fontSize: '11.2px' }}>
                                {item.goalSummary}
                              </div>
                              <div className="flex items-center gap-1">
                                <Target className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-500" style={{ fontSize: '9.6px' }}>
                                  {item.goalType}
                                </span>
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-sm">{item.goalSummary}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    
                    <TableCell>
                      {getAiSuggestionBadge(item.aiSuggestion)}
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`${getConfidenceColor(item.confidence)}`} style={{ fontSize: '11.2px', fontWeight: 500 }}>
                          {item.confidence}%
                        </span>
                        <div className="w-16 bg-gray-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full ${item.confidence >= 90 ? 'bg-green-600' : item.confidence >= 80 ? 'bg-yellow-600' : 'bg-red-600'}`}
                            style={{ width: `${item.confidence}%` }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      {getStatusBadge(item.status)}
                    </TableCell>
                    
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        style={{ fontSize: '9.6px' }}
                        onClick={(event) => handleViewClick(item, event)}
                      >
                        <FileSearch className="w-3 h-3 mr-1" />
                        View Goals
                      </Button>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row Content */}
                  {expandedRows.has(item.id) && (
                    <TableRow className="bg-gray-50">
                      <TableCell colSpan={9}>
                        <div className="py-4 px-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-gray-900 mb-2" style={{ fontSize: '11.2px', fontWeight: 600 }}>
                                Detailed Context
                              </h4>
                              <p className="text-gray-700" style={{ fontSize: '10px', lineHeight: 1.5 }}>
                                {item.expandedContent}
                              </p>
                            </div>
                            <div>
                              <h4 className="text-gray-900 mb-2" style={{ fontSize: '11.2px', fontWeight: 600 }}>
                                Audit Information
                              </h4>
                              <div className="space-y-2" style={{ fontSize: '10px' }}>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Last Modified:</span>
                                  <span className="text-gray-900">
                                    {new Date(item.lastModified).toLocaleDateString()} {new Date(item.lastModified).toLocaleTimeString()}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Goal Type:</span>
                                  <span className="text-gray-900">{item.goalType}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">AI Confidence:</span>
                                  <span className={`${getConfidenceColor(item.confidence)} font-medium`}>
                                    {item.confidence}%
                                  </span>
                                </div>
                              </div>
                              
                              {/* Click to view goals hint */}
                              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <FileSearch className="w-4 h-4 text-blue-600" />
                                  <span className="text-blue-800" style={{ fontSize: '10px', fontWeight: 500 }}>
                                    Click anywhere on this row to view detailed audit goals
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Empty State */}
        {data.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-2">
              <Target className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-gray-500" style={{ fontSize: '11.2px' }}>
              No items match the current filters
            </p>
            <p className="text-gray-400" style={{ fontSize: '9.6px' }}>
              Try adjusting your filter criteria to see more results
            </p>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-gray-500 whitespace-nowrap" style={{ fontSize: '11.2px' }}>
            Page {currentPage} of {totalPages}
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              
              {/* Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => onPageChange(pageNum)}
                      isActive={currentPage === pageNum}
                      className="cursor-pointer"
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}